import React, {
  useEffect,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { Box } from 'grommet';
import GameActionBar from './GameActionBar';
import CreateNewGame from './CreateNewGame';
import GameActionSheet from './GameActionSheet';
import NoScoringKeyModal from '@features/TeamPage/NoScoringKeyModal';
import SHDContainer from '@ui/Container/SHDContainer';
import SHDText from '@ui/Text/SHDText';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import UpcomingCardList from './UpcomingCardList';
import CameraSelectionSheet from './CameraSelectionSheet';
import ExitScoringModal from '@features/Scoring/ExitScoringModal';

// Helpers and utilities
import { ResponsiveContext } from 'grommet';
import { useHistory } from 'react-router-dom';
import { useModal } from '@components/ModalContext';
import { useTray } from '@contexts/TrayContext';
import useGame from '@hooks/useGame';
import useTeamGames from '@hooks/useTeamGames';
import useScoringKey from '@hooks/useScoringKey';
import { TeamContext } from '@services/Session/context';
import useEventsGameStart from '@hooks/useEventsGameStart';

// Types and interfaces
import { ScheduleStatus, GameActions, GameStartEvent } from './schedule.d';
import type { ScheduleProps } from './schedule.d';
import type { BoxExtendedProps } from 'grommet';
import type { TeamGameResponse } from '@dto/api';

const Schedule: React.FC<ScheduleProps> = ({
  teamId,
  teamName,
  isScoringLink,
  isIScore = false,
  isAdmin = false,
  hasRoster,
  sportType,
  headerHeight,
}) => {
  const queryParams = new URLSearchParams(location.search);
  const rosterParam = queryParams.get('roster');
  const { error, loading } = useTeamGames(teamId, !!rosterParam);
  const teamStore = useContext(TeamContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [height, setHeight] = useState<string>('auto');
  const pastGamesRef = useRef<HTMLDivElement>(null);
  const upcomingGamesRef = useRef<HTMLDivElement>(null);
  const todayGamesRef = useRef<HTMLDivElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  const { endGame, getScoreGameUrl, getSetLineupUrl } = useGame();
  const { showModal, hideModal } = useModal();
  const { showTray, hideTray } = useTray();
  const { trackGameStartEvent } = useEventsGameStart(
    teamId,
    sportType || '',
    isAdmin
  );
  const [pastGames, setPastGames] = useState<TeamGameResponse[]>([]);
  const [todayGames, setTodayGames] = useState<TeamGameResponse[]>([]);
  const [upcomingGames, setUpcomingGames] = useState<TeamGameResponse[]>([]);
  const history = useHistory();
  const viewportSize = useContext(ResponsiveContext);

  const redirectTrayClose = () => {
    history.replace({
      pathname: `/${teamId}`,
      state: { teamId },
    });
    window.location.reload();
  };

  const handleNoScoringKeyTray = () => {
    showTray(
      <NoScoringKeyModal
        onClose={() => {
          hideTray();
          redirectTrayClose();
        }}
      />,
      {
        onClose: () => {
          hideTray();
          redirectTrayClose();
        },
      }
    );
  };

  const userAuthorizedByScoringKey = useScoringKey(
    teamId,
    isScoringLink as boolean,
    handleNoScoringKeyTray
  );

  const handlePropUpdates = useCallback((): BoxExtendedProps => {
    if (viewportSize === 'large' || viewportSize === 'xlarge') {
      return {
        wrap: true,
        flex: true,
        direction: 'row',
        justify: 'between',
      };
    }
    return { direction: 'column' };
  }, [viewportSize]);

  const hideGameTray = (abandoned: boolean = true) => {
    if (abandoned) {
      trackGameStartEvent(GameStartEvent.GAME_CREATION_ABANDONED);
    }
  };

  const gameSheetEdit = (game: TeamGameResponse) => {
    return (
      <CreateNewGame
        teamId={teamId as string}
        mode="edit"
        onClose={(abandoned) => {
          hideTray();
          hideGameTray(abandoned);
        }}
        game={game}
        sportType={sportType || ''}
      />
    );
  };

  const gameSheetAction = (game: TeamGameResponse) => {
    trackGameStartEvent(GameStartEvent.GAME_ACTION_SHEET_OPENED, {
      gameId: game.id,
      role: 'team_manager',
      scoring_link: isScoringLink,
      current_status: status,
    });
    return (
      <GameActionSheet
        status={game.scoreGameState}
        isScoringLink={isScoringLink}
        game={game}
        onClick={(action, gameInfo) => {
          trackGameStartEvent(GameStartEvent.GAME_ACTION_SELECTED, {
            gameId: gameInfo.id,
            scoring_link: isScoringLink,
            action_taken: action,
          });
          switch (action) {
            case GameActions.EDIT_GAME:
              hideTray();
              showTray(gameSheetEdit(game), { onClose: hideTray });
              break;
            case GameActions.GO_LIVE:
              hideTray();
              showTray(
                <CameraSelectionSheet
                  teamId={teamId}
                  teamName={gameInfo.scoreOurName}
                  game={gameInfo}
                  onCloseTray={hideTray}
                  onClick={(type) => {
                    hideTray();
                    if (type === 'settings') {
                      history.push({
                        pathname: `/team_settings/camera`,
                      });
                    }
                  }}
                />,
                {
                  onClose: hideTray,
                }
              );
              trackGameStartEvent(GameStartEvent.GO_LIVE_BUTTON_CLICKED, {
                //hasDestination, replace when team response includes if user is synced with fb or yt
                hasRoster,
                context: isIScore ? 'schedule_tab' : 'game_action',
              });
              break;
            case GameActions.START_SCORING:
            case GameActions.RESUME_SCORING:
              hideTray();
              getScoreGameUrl(teamId, gameInfo);
              break;
            case GameActions.SET_LINEUP:
              hideTray();
              history.push(getSetLineupUrl(teamId, gameInfo));
              break;
            case GameActions.END_GAME:
              showModal(
                <ExitScoringModal
                  hideModal={hideModal}
                  onPauseScoring={() => {
                    hideModal();
                    hideTray();
                  }}
                  onEndGame={() => {
                    endGame(teamId, gameInfo.id as string);
                    hideModal();
                    hideTray();
                    history.push(`/${teamId}`);
                  }}
                />,
                {
                  onClose: () => hideModal(),
                  onGoBack: undefined,
                  isRelativeHeight: true,
                  isConfirmation: true,
                }
              );
              break;
            default:
              break;
          }
        }}
      />
    );
  };

  const handleCardClick = (status: ScheduleStatus, game: TeamGameResponse) => {
    trackGameStartEvent(GameStartEvent.GAME_CARD_CLICKED, {
      gameId: game?.id,
      current_status: status,
    });
    if (isAdmin || userAuthorizedByScoringKey) {
      switch (status) {
        case ScheduleStatus.UPCOMING:
        case ScheduleStatus.TODAY:
        case ScheduleStatus.PAST:
          if (game.isEnded) {
            history.push(`/game/${game.id}`);
          } else {
            !isIScore && showTray(gameSheetAction(game), { onClose: hideTray });
          }
          break;
        default:
          history.push(`/game/${game.id}`);
          break;
      }
    } else {
      switch (status) {
        case ScheduleStatus.LIVE:
          history.push(`/live/${teamId}`);
          break;
        case ScheduleStatus.UPCOMING:
        case ScheduleStatus.TODAY:
          if (game?.scoreTsStart > 0) {
            history.push(`/game/${game.id}`);
          }
          break;
        default:
          history.push(`/game/${game.id}`);
          break;
      }
    }
  };

  useEffect(() => {
    const { past, upcoming, today } = teamStore.fetchSchedule();
    setPastGames(past);
    setUpcomingGames(upcoming);
    setTodayGames(today);
  }, [teamStore, teamStore.schedule]);

  useEffect(() => {
    if (!loading) {
      // Set scroll padding based on screen size
      const SCROLL_PADDING_TOP =
        viewportSize === 'small' ? 52 : viewportSize === 'medium' ? 62 : 32;

      // Update container height
      const totalHeight = window.innerHeight - (headerHeight ?? 0);
      setHeight(`${totalHeight}px`);

      requestAnimationFrame(() => {
        if (todayGames.length > 0 && todayGamesRef.current) {
          const elementTodayTop =
            todayGamesRef.current.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementTodayTop - (headerHeight ?? 0) - SCROLL_PADDING_TOP,
            behavior: 'instant',
          });
        } else if (upcomingGamesRef.current) {
          const elementUpcomingTop =
            upcomingGamesRef.current.getBoundingClientRect().top +
            window.scrollY;

          window.scrollTo({
            top: elementUpcomingTop - (headerHeight ?? 0) - SCROLL_PADDING_TOP,
            behavior: 'instant',
          });
        }
      });
    }
  }, [headerHeight, upcomingGames, todayGames, loading, viewportSize]);

  const calcBottomPadding = () => {
    let cardHeight = 220;

    if (viewportSize === 'small') {
      cardHeight = 150;
    } else if (viewportSize === 'medium') {
      cardHeight = 170;
    }

    const extraCards = 2;
    const padding = cardHeight * Math.min(pastGames.length, extraCards);

    return `${Math.max(padding, 0)}px`;
  };

  return (
    <SHDThemeOverride full={false}>
      {(isAdmin || userAuthorizedByScoringKey) &&
        (isIScore && isScoringLink ? null : (
          <GameActionBar
            teamId={teamId}
            teamName={teamName}
            sportType={sportType as string}
            isIScore={isIScore}
            hasRoster={hasRoster}
          />
        ))}

      <SHDContainer
        background="gray-50"
        pad={{
          horizontal: 'medium',
          bottom: calcBottomPadding(),
          top: 0,
        }}
        parentRef={parentRef}
      >
        {!loading && (
          <>
            <UpcomingCardList
              scheduleStatus={ScheduleStatus.TODAY}
              upcomingGames={upcomingGames}
              todayGames={todayGames}
              pastGames={pastGames}
              teamId={teamId}
              handlePropUpdates={handlePropUpdates}
              onClick={handleCardClick}
              todayListRef={todayGamesRef}
              upcomingListRef={upcomingGamesRef}
              pastListRef={pastGamesRef}
              isIScore={isIScore}
              isAdmin={isAdmin}
              isScoringLink={isScoringLink || false}
            />
          </>
        )}
        {error && (
          <Box align="center" width="100%">
            <SHDText color="red">{error}</SHDText>
          </Box>
        )}
      </SHDContainer>
    </SHDThemeOverride>
  );
};

export default Schedule;
